import { Injectable } from '@angular/core';

declare const gtag: Function;

export type ApplyFunnelStep = 'view' | 'start' | 'submit_attempt' | 'submit_fail' | 'submit_success';

export interface ApplyFunnelData {
  src?: string;
  formType?: string;      // apply | inquire | quick-apply
  jobId?: number | string;
  profession?: string;
  reason?: string;        // submit_fail only
}

/**
 * Apply-page funnel tracking. Each step posts a small row to
 * /api/applicants/funnel so views -> starts -> submits can be reported per
 * traffic source (server/scripts/applyFunnelReport.js), and mirrors the step
 * to GA4 as an `apply_funnel` event. Fire-and-forget: never blocks or throws.
 */
@Injectable({
  providedIn: 'root'
})
export class ApplyFunnelService {
  private readonly _route = '/api/applicants/funnel';
  private readonly _sessionKey = 'edu_apply_sid';
  private _sessionId: string = null;
  private _fired = new Set<string>(); // de-dupe view/start per session + job + form

  track(step: ApplyFunnelStep, data: ApplyFunnelData = {}) {
    try {
      // view/start are once-per-form-instance signals; submits can legitimately repeat.
      if (step === 'view' || step === 'start') {
        const key = `${step}|${data.formType || ''}|${data.jobId || ''}`;
        if (this._fired.has(key)) return;
        this._fired.add(key);
      }

      const payload = {
        event: step,
        src: data.src || null,
        formType: data.formType || null,
        jobId: data.jobId ? Number(data.jobId) : null,
        profession: data.profession || null,
        reason: data.reason || null,
        sessionId: this.getSessionId(),
        path: window.location.pathname,
        referrer: document.referrer || null
      };

      // keepalive so a submit_success fired right before navigation still lands.
      fetch(this._route, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch(() => {});

      if (typeof gtag === 'function') {
        gtag('event', 'apply_funnel', {
          step,
          source: payload.src || 'direct',
          form_type: payload.formType || '',
          job_id: payload.jobId || '',
          reason: payload.reason || ''
        });
      }
    } catch (_e) {
      // tracking must never affect the apply flow
    }
  }


  private getSessionId(): string {
    if (this._sessionId) return this._sessionId;
    try {
      let sid = sessionStorage.getItem(this._sessionKey);
      if (!sid) {
        sid = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
        sessionStorage.setItem(this._sessionKey, sid);
      }
      this._sessionId = sid;
    } catch (_e) {
      this._sessionId = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    }
    return this._sessionId;
  }
}
